/** Root application: routes between login and the IDE layout. */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthContext } from "./hooks/AuthContext";
import { useTheme } from "./hooks/ThemeContext";
import { useDebouncedCallback } from "./hooks/useDebounce";
import { LoginPage } from "./components/LoginPage";
import { FileExplorer } from "./components/FileExplorer";
import { CodeMirrorEditor } from "./components/CodeMirrorEditor";
import { MonacoDiffViewer } from "./components/MonacoDiffViewer";
import { PdfViewer, type PdfViewerHandle } from "./components/PdfViewer";
import { DraftPreview } from "./components/DraftPreview";
import { ProblemsPanel } from "./components/ProblemsPanel";
import { GitPanel } from "./components/GitPanel";
import { HelpPanel } from "./components/HelpPanel";
import { CommentsPanel } from "./components/CommentsPanel";
import { Splitter } from "./components/Splitter";
import { AnnotationsService, CompileService, FilesService, GitService } from "./services";
import type {
  Annotation,
  AnnotationInput,
  CompileResult,
  FileNode,
  LatexError,
  SaveStatus,
} from "./types";

type BottomTab = "problems" | "git" | "comments" | "log" | "help";

export default function App(): JSX.Element {
  const { user, loading, logout } = useAuthContext();
  const { theme, toggle } = useTheme();
  const [tree, setTree] = useState<FileNode | null>(null);
  const [activePath, setActivePath] = useState<string | null>(null);
  const [rootFile, setRootFile] = useState<string | null>(null);
  const [diffView, setDiffView] = useState<{ path: string; original: string; modified: string } | null>(null);
  const [content, setContent] = useState("");
  const [savedContent, setSavedContent] = useState("");
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("saved");
  const [compileResult, setCompileResult] = useState<CompileResult | null>(null);
  const [errors, setErrors] = useState<LatexError[]>([]);
  const [bottomTab, setBottomTab] = useState<BottomTab>("problems");
  const [gitRefreshKey, setGitRefreshKey] = useState(0);
  const [explorerWidth, setExplorerWidth] = useState(240);
  const [explorerCollapsed, setExplorerCollapsed] = useState(false);
  const [editorWidth, setEditorWidth] = useState(0.5); // fraction
  const [bottomHeight, setBottomHeight] = useState(200);
  const [bottomCollapsed, setBottomCollapsed] = useState(false);
  const pdfViewerRef = useRef<PdfViewerHandle>(null);
  const synctexTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [selectedAnnId, setSelectedAnnId] = useState<number | null>(null);
  const [previewMode, setPreviewMode] = useState<"draft" | "pdf">("draft");
  const [cursorLine, setCursorLine] = useState<number | null>(null);
  const [targetJump, setTargetJump] = useState<{ line: number; id: number } | null>(null);

  const refreshAnnotations = useCallback(async () => {
    try {
      const list = await AnnotationsService.list(undefined, true, true);
      setAnnotations(list);
    } catch (err) {
      console.error("Failed to load annotations", err);
    }
  }, []);

  // Load annotations on login
  useEffect(() => {
    if (user) void refreshAnnotations();
  }, [user, refreshAnnotations]);

  const handleCreateAnnotation = useCallback(
    async (input: AnnotationInput) => {
      try {
        const created = await AnnotationsService.create(input);
        setAnnotations((prev) => [...prev, created]);
        setSelectedAnnId(created.id);
      } catch (err) {
        console.error("Failed to create annotation", err);
      }
    },
    [],
  );

  const handleResolveAnnotation = useCallback(
    async (id: number, resolved: boolean) => {
      try {
        const updated = await AnnotationsService.resolve(id, resolved);
        setAnnotations((prev) => prev.map((a) => (a.id === id ? updated : a)));
      } catch (err) {
        console.error("Failed to resolve annotation", err);
      }
    },
    [],
  );

  const handleReplyAnnotation = useCallback(
    async (id: number, content: string) => {
      try {
        const reply = await AnnotationsService.reply(id, content);
        setAnnotations((prev) =>
          prev.map((a) =>
            a.id === id ? { ...a, replies: [...a.replies, reply] } : a,
          ),
        );
      } catch (err) {
        console.error("Failed to reply to annotation", err);
      }
    },
    [],
  );

  const handleDeleteAnnotation = useCallback(
    async (id: number) => {
      try {
        await AnnotationsService.remove(id);
        void refreshAnnotations();
      } catch (err) {
        console.error("Failed to delete annotation", err);
      }
    },
    [refreshAnnotations],
  );

  const handleRestoreAnnotation = useCallback(
    async (id: number) => {
      try {
        const restored = await AnnotationsService.restore(id);
        setAnnotations((prev) => prev.map((a) => (a.id === id ? restored : a)));
      } catch (err) {
        console.error("Failed to restore annotation", err);
      }
    },
    [],
  );

  const handleSelectAnnotation = useCallback((id: number) => {
    setSelectedAnnId(id);
    setBottomTab("comments");
    setBottomCollapsed(false);
  }, []);

  const refreshTree = useCallback(async () => {
    try {
      const t = await FilesService.tree();
      setTree(t);
    } catch (err) {
      console.error("Tree load error", err);
    }
  }, []);

  // Load tree on login.
  useEffect(() => {
    if (user) refreshTree();
  }, [user, refreshTree]);

  const openFile = useCallback(async (path: string): Promise<void> => {
    try {
      const file = await FilesService.content(path);
      setDiffView(null);
      setActivePath(path);
      setContent(file.content);
      setSavedContent(file.content);
      setSaveStatus("saved");
    } catch (err) {
      console.error("Open file error", err);
    }
  }, []);

  const saveFile = useCallback(
    async (path: string, body: string): Promise<void> => {
      setSaveStatus("saving");
      try {
        await FilesService.write(path, body);
        setSavedContent(body);
        setSaveStatus("saved");
      } catch (err) {
        console.error("Save error", err);
        setSaveStatus("error");
      }
    },
    [],
  );

  const [compiling, setCompiling] = useState(false);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const compile = useCallback(async (path: string): Promise<void> => {
    setCompiling(true);
    try {
      // Compile started in background — poll for status.
      const poll = async (): Promise<void> => {
        try {
          const st = await CompileService.status(path);
          if (st.status === "running") {
            pollTimer.current = setTimeout(poll, 1500);
            return;
          }
          // Done or error — update UI.
          setCompileResult({
            success: st.success,
            pdf_url: st.pdf_url,
            log: st.log,
            errors: st.errors,
            duration_ms: st.duration_ms,
            compile_id: st.compile_id,
            status: st.status as CompileResult["status"],
          });
          setErrors(st.errors);
          if (st.errors.length > 0) {
            setBottomTab("problems");
            setBottomCollapsed(false);
          }
          setCompiling(false);
        } catch (err) {
          console.error("Compile status poll error", err);
          setCompiling(false);
        }
      };

      const result = await CompileService.compile(path);
      // If busy (another compile already running), continue polling until it completes.
      if (result.status === "busy") {
        pollTimer.current = setTimeout(poll, 1500);
        return;
      }
      pollTimer.current = setTimeout(poll, 1500);
    } catch (err) {
      console.error("Compile error", err);
      setErrors([
        {
          file: path,
          line: null,
          message: err instanceof Error ? err.message : "Compilation failed",
          severity: "error",
        },
      ]);
      setBottomTab("problems");
      setBottomCollapsed(false);
      setCompiling(false);
    }
  }, []);

  // Auto-detect root .tex file (main.tex), open it and auto-compile on load / login / refresh.
  const hasAutoCompiled = useRef(false);
  useEffect(() => {
    if (!user) {
      hasAutoCompiled.current = false;
      return;
    }
    if (!tree || hasAutoCompiled.current) return;
    const findMain = (node: FileNode): string | null => {
      for (const child of node.children) {
        if (child.type === "file" && child.name === "main.tex") {
          return child.path;
        }
        if (child.type === "directory") {
          const found = findMain(child);
          if (found) return found;
        }
      }
      return null;
    };
    const main = findMain(tree);
    if (main) {
      hasAutoCompiled.current = true;
      setRootFile(main);
      void openFile(main);
      // Immediately check existing PDF status to show preview right away.
      CompileService.status(main)
        .then((st) => {
          if (st.pdf_url && st.success) {
            setCompileResult({
              success: st.success,
              pdf_url: st.pdf_url,
              log: st.log,
              errors: st.errors,
              duration_ms: st.duration_ms,
              compile_id: st.compile_id,
              status: st.status as CompileResult["status"],
            });
          }
        })
        .catch(console.error);
      // Automatically trigger compile on initial load.
      void compile(main);
    }
  }, [user, tree, openFile, compile]);

  // Autosave file to disk on keystroke without triggering heavy XeLaTeX compile.
  // Live Draft preview renders instantly in client memory.
  const debouncedSave = useDebouncedCallback((path: string, body: string) => {
    void saveFile(path, body);
  }, 700);

  const handleContentChange = useCallback(
    (body: string): void => {
      if (!activePath) return;
      setContent(body);
      if (body !== savedContent) setSaveStatus("unsaved");
      debouncedSave(activePath, body);
    },
    [activePath, savedContent, debouncedSave],
  );

  const handleSave = useCallback((): void => {
    if (!activePath) return;
    void saveFile(activePath, content);
  }, [activePath, content, saveFile]);

  const handleCompile = useCallback((): void => {
    if (!activePath) return;
    void saveFile(activePath, content).then(() => {
      const target = rootFile ?? activePath;
      setPreviewMode("pdf");
      void compile(target);
    });
  }, [activePath, content, saveFile, compile, rootFile]);

  // SyncTeX forward & Live Draft sync: when the cursor moves in the editor,
  // update cursorLine immediately for Live Draft sync, and debounce-call SyncTeX for PDF.
  const handleCursorChange = useCallback(
    (line: number, column: number): void => {
      setCursorLine(line);
      if (!activePath || !rootFile) return;
      if (synctexTimer.current) clearTimeout(synctexTimer.current);
      synctexTimer.current = setTimeout(async () => {
        try {
          const result = await CompileService.synctexForward(
            rootFile,
            activePath,
            line,
            column,
          );
          pdfViewerRef.current?.goToPosition(result.page, result.x, result.y);
        } catch {
          // SyncTeX may fail if no synctex data; silently ignore.
        }
      }, 500);
    },
    [activePath, rootFile],
  );

  const handleNavigateToLine = useCallback((line: number): void => {
    setTargetJump({ line, id: Date.now() });
    setCursorLine(line);
  }, []);

  const handleJumpToError = useCallback(
    (file: string, line: number | null): void => {
      // Open the referenced file (best effort: relative name match).
      void openFile(file).then(() => {
        if (line !== null && line > 0) {
          setTargetJump({ line, id: Date.now() });
          setCursorLine(line);
        }
      });
    },
    [openFile],
  );

  const handleCommitPush = useCallback((): void => {
    setBottomTab("git");
    setBottomCollapsed(false);
    setGitRefreshKey((k) => k + 1);
  }, []);

  const handleOpenFileDiff = useCallback(
    async (path: string): Promise<void> => {
      try {
        const origRes = await GitService.fileOriginal(path);
        let currentContent = "";
        if (activePath === path) {
          currentContent = content;
        } else {
          try {
            const fileRes = await FilesService.content(path);
            currentContent = fileRes.content;
          } catch {
            currentContent = "";
          }
        }
        setDiffView({
          path,
          original: origRes.content || "",
          modified: currentContent || "",
        });
      } catch (err) {
        console.error("Failed to load file diff", err);
      }
    },
    [activePath, content],
  );

  const handleGitReset = useCallback(async (): Promise<void> => {
    if (!confirm("This will discard ALL uncommitted changes in the workspace. Are you sure?")) {
      return;
    }
    try {
      await GitService.reset();
      setGitRefreshKey((k) => k + 1);
      if (activePath) {
        const fresh = await FilesService.content(activePath);
        setContent(fresh.content);
        setSavedContent(fresh.content);
        setSaveStatus("saved");
      }
    } catch (err) {
      console.error("Git reset error", err);
      alert(err instanceof Error ? err.message : "Git reset failed");
    }
  }, [activePath]);

  if (loading) {
    return <div className="empty">Loading...</div>;
  }
  if (!user) {
    return <LoginPage />;
  }

  const compileTarget = rootFile ?? activePath ?? "";
  const pdfUrl = compileResult?.success && compileResult.pdf_url
    ? CompileService.pdfUrl(compileTarget)
    : null;

  return (
    <div className="app-layout">
      <div className="header">
        <button
          className="sidebar-toggle-btn"
          onClick={() => setExplorerCollapsed((c) => !c)}
          title={explorerCollapsed ? "Show File Explorer" : "Hide File Explorer"}
          style={{ marginRight: "4px" }}
        >
          {explorerCollapsed ? "◫ Explorer" : "◧ Explorer"}
        </button>
        <span className="title">LaTeX IDE</span>
        <button onClick={handleSave} disabled={!activePath}>Save</button>
        <button onClick={handleCompile} disabled={!activePath || compiling}>
          {compiling ? (
            <span className="spinner-text">
              <span className="spinner" /> Compiling...
            </span>
          ) : (
            `Compile${rootFile ? ` (${rootFile.split("/").pop()})` : ""}`
          )}
        </button>
        <button onClick={handleCommitPush}>Git</button>
        <button
          onClick={() => void handleGitReset()}
          title="Discard all uncommitted changes and reset to last commit"
          style={{ color: "var(--error)" }}
        >
          Reset
        </button>
        <span className="spacer" />
        <span className={`save-status ${saveStatus}`}>
          {saveStatus === "saved" && "Saved"}
          {saveStatus === "saving" && "Saving..."}
          {saveStatus === "unsaved" && "Unsaved"}
          {saveStatus === "error" && "Save error"}
        </span>
        <span className="user">{user.username}</span>
        <button
          className="theme-toggle"
          title="Toggle light/dark theme"
          onClick={toggle}
        >
          {theme === "dark" ? "☀ Light" : "☾ Dark"}
        </button>
        <button onClick={() => void logout()}>Sign out</button>
      </div>

      <div className="body">
        {explorerCollapsed ? (
          <div
            className="explorer-collapsed-strip"
            onClick={() => setExplorerCollapsed(false)}
            title="Show File Explorer"
          >
            <span className="collapsed-icon">📁</span>
            <span className="collapsed-label">Files</span>
          </div>
        ) : (
          <>
            <div style={{ width: explorerWidth, flexShrink: 0 }}>
              {tree && (
                <FileExplorer
                  tree={tree}
                  activePath={activePath}
                  onOpen={(p) => void openFile(p)}
                  onRefresh={() => void refreshTree()}
                  onCollapse={() => setExplorerCollapsed(true)}
                />
              )}
            </div>
            <Splitter
              direction="horizontal"
              onResize={(d) => setExplorerWidth((w) => Math.max(160, Math.min(500, w + d)))}
            />
          </>
        )}
        <div style={{ flex: editorWidth, display: "flex", minWidth: 0, minHeight: 0 }}>
          {diffView ? (
            <MonacoDiffViewer
              path={diffView.path}
              original={diffView.original}
              modified={diffView.modified}
              onClose={() => setDiffView(null)}
            />
          ) : activePath ? (
            <CodeMirrorEditor
              path={activePath}
              content={content}
              onContentChange={handleContentChange}
              onSave={handleSave}
              saveStatus={saveStatus}
              onCursorChange={handleCursorChange}
              targetLine={targetJump}
            />
          ) : (
            <div className="editor-pane">
              <div className="editor-tabs" />
              <div className="empty">Open a file to start editing.</div>
            </div>
          )}
        </div>
        <Splitter
          direction="horizontal"
          onResize={(d) =>
            setEditorWidth((f) => Math.max(0.2, Math.min(0.8, f + d / window.innerWidth)))
          }
        />
        <div className="pdf-pane" style={{ flex: 1 - editorWidth, display: "flex", flexDirection: "column", minWidth: 0 }}>
          <div className="preview-mode-bar">
            <button
              type="button"
              className={`preview-mode-tab${previewMode === "draft" ? " active" : ""}`}
              onClick={() => setPreviewMode("draft")}
              title="پیش‌نمایش زنده و سریع در مرورگر"
            >
              ⚡ پیش‌نمایش زنده (Live Draft)
            </button>
            <button
              type="button"
              className={`preview-mode-tab${previewMode === "pdf" ? " active" : ""}`}
              onClick={() => setPreviewMode("pdf")}
              title="مشاهده پی‌دی‌اف نهایی حاصل از کامپایل XeLaTeX"
            >
              📄 پی‌دی‌اف نهایی (PDF)
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex" }}>
            {previewMode === "draft" ? (
              <DraftPreview
                content={content}
                filePath={activePath ?? ""}
                cursorLine={cursorLine}
                onNavigateToLine={handleNavigateToLine}
              />
            ) : (
              <PdfViewer
                ref={pdfViewerRef}
                pdfUrl={pdfUrl}
                document={compileTarget}
                compileId={compileResult?.compile_id ?? ""}
                annotations={annotations}
                selectedAnnId={selectedAnnId}
                onCreateAnnotation={handleCreateAnnotation}
                onSelectAnnotation={handleSelectAnnotation}
              />
            )}
          </div>
        </div>
      </div>

      {!bottomCollapsed && (
        <Splitter
          direction="vertical"
          onResize={(d) => setBottomHeight((h) => Math.max(80, Math.min(500, h - d)))}
        />
      )}
      <div className={`bottom-panel${bottomCollapsed ? " collapsed" : ""}`} style={{ height: bottomCollapsed ? "auto" : bottomHeight }}>
        <div className="bottom-tabs">
          <div
            className={`bottom-tab${bottomTab === "problems" && !bottomCollapsed ? " active" : ""}`}
            onClick={() => {
              setBottomTab("problems");
              setBottomCollapsed(false);
            }}
          >
            Problems {errors.length > 0 && `(${errors.length})`}
          </div>
          <div
            className={`bottom-tab${bottomTab === "comments" && !bottomCollapsed ? " active" : ""}`}
            onClick={() => {
              setBottomTab("comments");
              setBottomCollapsed(false);
            }}
          >
            Comments{" "}
            {annotations.filter((a) => !a.is_deleted && !a.resolved).length > 0 &&
              `(${annotations.filter((a) => !a.is_deleted && !a.resolved).length})`}
          </div>
          <div
            className={`bottom-tab${bottomTab === "git" && !bottomCollapsed ? " active" : ""}`}
            onClick={() => {
              setBottomTab("git");
              setBottomCollapsed(false);
            }}
          >
            Source Control
          </div>
          <div
            className={`bottom-tab${bottomTab === "log" && !bottomCollapsed ? " active" : ""}`}
            onClick={() => {
              setBottomTab("log");
              setBottomCollapsed(false);
            }}
          >
            Log
          </div>
          <div
            className={`bottom-tab${bottomTab === "help" && !bottomCollapsed ? " active" : ""}`}
            onClick={() => {
              setBottomTab("help");
              setBottomCollapsed(false);
            }}
          >
            Help
          </div>
          <span style={{ flex: 1 }} />
          <button
            className="bottom-toggle-btn"
            onClick={() => setBottomCollapsed((c) => !c)}
            title={bottomCollapsed ? "Expand panel" : "Collapse panel"}
          >
            {bottomCollapsed ? "▲ Expand" : "▼ Hide"}
          </button>
        </div>
        {!bottomCollapsed && (
          <div className="bottom-content">
            {bottomTab === "problems" && (
              <ProblemsPanel errors={errors} onJump={handleJumpToError} />
            )}
            {bottomTab === "comments" && (
              <CommentsPanel
                annotations={annotations}
                activeDocument={compileTarget || activePath}
                onJumpToPage={(p) => pdfViewerRef.current?.goToPage(p)}
                onResolve={handleResolveAnnotation}
                onReply={handleReplyAnnotation}
                onDelete={handleDeleteAnnotation}
                onRestore={handleRestoreAnnotation}
                onRefresh={refreshAnnotations}
              />
            )}
            {bottomTab === "git" && (
              <GitPanel
                refreshKey={gitRefreshKey}
                onOpenFileDiff={handleOpenFileDiff}
              />
            )}
            {bottomTab === "log" && (
              <pre>{compileResult?.log ?? "No compilation log yet."}</pre>
            )}
            {bottomTab === "help" && <HelpPanel />}
          </div>
        )}
      </div>
    </div>
  );
}
